import React, { useEffect, useState } from "react";
import reqCaixa from "../../../../services/reqMultiTenant/reqCaixa";
import Loading from "../../../../components/loading";
import libFloat from "../../../../util/libFloat";
import functions from "../../../../util/functions";
import Notification from "../../../../components/toast";
import HeaderReport from "../../../../components/header/report";
import { generateFechamentoPDF } from "../../../../util/reports/reportFechamento";
import { Printer } from "lucide-react";

const Form = ({ isOpen, idTable, selectedData, handleClose }) => {
	const id = selectedData[idTable];

	const [loading, setLoading] = useState(false);
	const [dataFechamento, setDataFechamento] = useState(null);
	const [dataItens, setDataItens] = useState([]);
	const [dataOutroCaixa, setDataOutroCaixa] = useState([]);

	useEffect(() => {
		if (isOpen && id) {
			fetchData(id);
		} else {
			// Limpar dados ao fechar
			setDataFechamento(null);
			setDataItens([]);
			setDataOutroCaixa([]);
		}
	}, [isOpen, id]);

	const fetchData = async (idFechamento) => {
		try {
			setLoading(true);

			const [resFechamento, resItens, resOutro] = await Promise.all([
				reqCaixa.getFechamentoId(idFechamento),
				reqCaixa.getFechamentoItens(idFechamento),
				reqCaixa.getFechamentoOutroCaixa(idFechamento),
			]);

			// Se vier dentro de um array, pega o primeiro item. Caso contrário, pega o objeto.
			const fechamentoData = Array.isArray(resFechamento.data) ? resFechamento.data[0] : resFechamento.data;

			setDataFechamento(fechamentoData);
			setDataItens(resItens.data || []);
			setDataOutroCaixa(resOutro.data || []);
		} catch (error) {
			Notification.showToast({
				message: "Erro ao carregar detalhes do fechamento: " + error.message,
				type: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	const handlePrint = () => {
		generateFechamentoPDF({ dataFechamento, dataItens, dataOutroCaixa });
	};

	if (!isOpen) return null;

	return (
		<div className="flex flex-col bg-slate-100 h-full overflow-hidden">
			{/* HEADER PADRONIZADO (Movimentação) */}
			<header className="flex flex-col bg-white p-4 rounded-sm box-shadow-md mb-4 z-10">
				<div className="flex justify-between items-center">
					<div className="flex-1">
						<HeaderReport
							title="FECHAMENTO DE CAIXA"
							subTitle={`Número: ${dataFechamento?.numero_caixa || "-"} | Sessão: ${dataFechamento?.id_sessao || "-"}`}
						/>
					</div>
					<div className="flex justify-end items-center gap-2 h-full">
						<button
							className="btn btn-sm btn-outline-primary flex items-center gap-2 px-3"
							onClick={handlePrint}
							title="Imprimir Fechamento"
						>
							<Printer size={18} />
							<span>Imprimir</span>
						</button>
						<button className="btn btn-sm btn-outline-danger px-4" onClick={handleClose}>
							Fechar
						</button>
					</div>
				</div>
				<div className="border-bottom mt-3"></div>
			</header>

			{loading ? (
				<div className="flex flex-1 justify-center items-center">
					<Loading mensagem="Carregando dados do fechamento..." />
				</div>
			) : (
				<div className="flex-1 overflow-y-auto p-4 custom-scroll">
					<div className="flex flex-col gap-6 text-gray-700 pb-8">
						{/* DADOS DA SESSÃO - Melhorado com Grid Dashboard */}
						{dataFechamento && (
							<section className="bg-white p-5 rounded-sm box-shadow-md border-l-4 border-blue-600">
								<div className="flex items-center justify-between mb-4 border-bottom pb-3">
									<h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight">
										Dados da Sessão
									</h2>
									<div className="flex gap-2">
										<span className="badge bg-blue-100 text-blue-700 px-3 py-2 rounded-sm font-bold border border-blue-200">
											CAIXA: {dataFechamento.numero_caixa}
										</span>
										<span className="badge bg-slate-100 text-slate-600 px-3 py-2 rounded-sm font-bold border border-slate-200">
											SESSÃO: #{dataFechamento.id_sessao}
										</span>
									</div>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
									{/* Coluna 1: Operadores */}
									<div className="flex flex-col gap-3">
										<h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
											Responsáveis
										</h4>
										<div className="flex flex-col gap-2">
											<div className="flex flex-col">
												<span className="text-xs text-slate-500">Abertura:</span>
												<span className="font-semibold text-slate-800 uppercase">
													{dataFechamento.operador_abertura}
												</span>
											</div>
											<div className="flex flex-col">
												<span className="text-xs text-slate-500">Fechamento:</span>
												<span className="font-semibold text-slate-800 uppercase">
													{dataFechamento.operador_fechamento}
												</span>
											</div>
										</div>
									</div>

									{/* Coluna 2: Cronograma */}
									<div className="flex flex-col gap-3">
										<h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
											Cronograma
										</h4>
										<div className="flex flex-col gap-2">
											<div className="flex items-start gap-2">
												<div className="mt-1 h-3 w-3 rounded-full bg-green-500" />
												<div className="flex flex-col">
													<span className="text-xs text-slate-500">Início da Sessão:</span>
													<span className="font-semibold text-slate-800">
														{functions.dateTimeToStrIso8601(dataFechamento.data_abertura)}
													</span>
												</div>
											</div>
											<div className="flex items-start gap-2">
												<div className="mt-1 h-3 w-3 rounded-full bg-red-500" />
												<div className="flex flex-col">
													<span className="text-xs text-slate-500">Encerramento:</span>
													<span className="font-semibold text-slate-800">
														{functions.dateTimeToStrIso8601(dataFechamento.data_fechamento)}
													</span>
												</div>
											</div>
										</div>
									</div>

									{/* Coluna 3: Status / Resumo Rápido */}
									<div className="flex flex-col gap-3 p-3 bg-slate-50 rounded-sm border border-slate-100">
										<h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
											Resumo Operacional
										</h4>
										<div className="flex flex-col gap-2">
											<div className="flex justify-between items-center text-sm">
												<span className="text-slate-500">Diferença Final:</span>
												<span
													className={`font-bold ${
														dataFechamento.total_diferenca < 0
															? "text-red-600"
															: "text-green-600"
													}`}
												>
													{libFloat.strToCurrency(dataFechamento.total_diferenca)}
												</span>
											</div>
											<div className="flex justify-between items-center text-sm">
												<span className="text-slate-500">Registros:</span>
												<span className="font-bold text-slate-700">
													{dataItens.length} Formas de PGTO
												</span>
											</div>
										</div>
									</div>
								</div>
							</section>
						)}

						{/* VALOR EM DINHEIRO NO CAIXA (Padrão EntradasSaidasTable) */}
						{dataFechamento && (
							<section className="bg-white p-1 rounded-sm box-shadow-md overflow-hidden">
								<div className="table-responsive p-3 table-cl">
									<table className="table">
										<thead>
											<tr className="tr-cl border-bottom">
												<th scope="col">
													<h2 className="text-xl font-semibold">Movimentação em dinheiro</h2>
												</th>
												<th scope="col" className="text-end">
													Valor
												</th>
											</tr>
										</thead>
										<tbody>
											{dataFechamento.valor_abertura > 0 && (
												<tr>
													<td>+ Valor de Abertura</td>
													<td className="text-end">
														<small>{libFloat.strToCurrency(dataFechamento.valor_abertura)}</small>
													</td>
												</tr>
											)}
											{dataFechamento.vendas_dinheiro > 0 && (
												<tr>
													<td>+ Vendas em dinheiro</td>
													<td className="text-end">
														<small>{libFloat.strToCurrency(dataFechamento.vendas_dinheiro)}</small>
													</td>
												</tr>
											)}
											{dataFechamento.suprimento > 0 && (
												<tr>
													<td>+ Suprimento</td>
													<td className="text-end">
														<small>{libFloat.strToCurrency(dataFechamento.suprimento)}</small>
													</td>
												</tr>
											)}
											{dataFechamento.sangria > 0 && (
												<tr>
													<td>- Sangria</td>
													<td className="text-end text-red-500">
														<small>{libFloat.strToCurrency(dataFechamento.sangria)}</small>
													</td>
												</tr>
											)}
											{dataFechamento.pagamentos > 0 && (
												<tr>
													<td>- Pagamentos / saídas</td>
													<td className="text-end text-red-500">
														<small>{libFloat.strToCurrency(dataFechamento.pagamentos)}</small>
													</td>
												</tr>
											)}
											{dataFechamento.entregas > 0 && (
												<tr>
													<td>- Entregas</td>
													<td className="text-end text-red-500">
														<small>{libFloat.strToCurrency(dataFechamento.entregas)}</small>
													</td>
												</tr>
											)}
											<tr>
												<td className="fw-bold">Total em Dinheiro no Caixa</td>
												<td className="fw-bold text-end">
													<small>{libFloat.strToCurrency(dataFechamento.valor_esperado_dinheiro)}</small>
												</td>
											</tr>
										</tbody>
									</table>
									<p className="text-xs text-slate-500 mt-2">
										<em className="mt-2">
											* O Total em Dinheiro já considera todas as{" "}
											<strong>entradas e saídas em dinheiro</strong> da sessão. Use este total para
											conferir o valor físico existente no caixa.
										</em>
									</p>
								</div>
							</section>
						)}

						{/* VENDAS DO PERÍODO */}
						{dataFechamento && (
							<section className="bg-white p-1 rounded-sm box-shadow-md overflow-hidden">
								<div className="table-responsive p-3 table-cl">
									<table className="table">
										<thead>
											<tr className="tr-cl border-bottom">
												<th scope="col">
													<h2 className="text-xl font-semibold">Resumo de Vendas e Cobranças</h2>
												</th>
												<th scope="col" className="text-end">
													Valor
												</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>+ Vendas do período (Bruto)</td>
												<td className="text-end">
													<small>{libFloat.strToCurrency(dataFechamento.vendas_periodo)}</small>
												</td>
											</tr>
											{dataFechamento.recebimentos_periodo > 0 && (
												<tr>
													<td>+ Recebimentos cliente</td>
													<td className="text-end">
														<small>{libFloat.strToCurrency(dataFechamento.recebimentos_periodo)}</small>
													</td>
												</tr>
											)}
											<tr className="border-top">
												<td className="fw-bold">Total Movimentado pela Sessão (Cartão + Dinheiro)</td>
												<td className="fw-bold text-end">
													<small>{libFloat.strToCurrency(dataFechamento.total_movimentado)}</small>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</section>
						)}

						{/* CONFERÊNCIA FINAL */}
						{dataFechamento && (
							<section className="bg-white p-1 rounded-sm box-shadow-md overflow-hidden">
								<div className="table-responsive p-3 table-cl">
									<table className="table">
										<thead>
											<tr className="tr-cl border-bottom">
												<th scope="col">
													<h2 className="text-xl font-semibold">Conferência Operacional</h2>
												</th>
												<th scope="col" className="text-end">
													Valor
												</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>Total Entradas no Sistema</td>
												<td className="text-end">
													<small>{libFloat.strToCurrency(dataFechamento.total_sistema_conf)}</small>
												</td>
											</tr>
											<tr>
												<td>Total Informado pelo Operador</td>
												<td className="text-end">
													<small>{libFloat.strToCurrency(dataFechamento.total_conferido)}</small>
												</td>
											</tr>
											<tr className="border-top">
												<td className="fw-bold">Saldo de Diferença</td>
												<td
													className={`fw-bold text-end ${
														dataFechamento.total_diferenca < 0 ? "text-red-600" : "text-green-600"
													}`}
												>
													<small>{libFloat.strToCurrency(dataFechamento.total_diferenca)}</small>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</section>
						)}

						{/* CONFERÊNCIA POR FORMA DE PAGAMENTO */}
						{dataItens.length > 0 && (
							<section className="bg-white p-1 rounded-sm box-shadow-md overflow-hidden">
								<div className="table-responsive p-3 table-cl">
									<table className="table">
										<thead>
											<tr className="tr-cl border-bottom">
												<th scope="col">
													<h2 className="text-xl font-semibold">Conferência por Forma de Pagamento</h2>
												</th>
												<th scope="col" className="text-end">
													Sistema
												</th>
												<th scope="col" className="text-end">
													Conferido
												</th>
												<th scope="col" className="text-end">
													Diferença
												</th>
											</tr>
										</thead>
										<tbody>
											{dataItens.map((item, idx) => (
												<tr key={idx} className="tr-cscl">
													<td>
														<span className="font-semibold uppercase">{item.forma_pgto}</span>
														{item.obs && (
															<div className="text-xs text-gray-400 italic">Obs: {item.obs}</div>
														)}
													</td>
													<td className="text-end">
														<small>{libFloat.strToCurrency(item.valor_sistema)}</small>
													</td>
													<td className="text-end font-medium">
														<small>{libFloat.strToCurrency(item.valor_conferido)}</small>
													</td>
													<td
														className={`text-end font-bold ${
															item.diferenca < 0 ? "text-red-500" : "text-green-500"
														}`}
													>
														<small>{libFloat.strToCurrency(item.diferenca)}</small>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</section>
						)}

						{/* OUTRAS MOVIMENTAÇÕES (OUTRO_CAIXA) */}
						{dataOutroCaixa.length > 0 && (
							<section className="bg-white p-1 rounded-sm box-shadow-md overflow-hidden">
								<div className="table-responsive p-3 table-cl">
									<table className="table">
										<thead>
											<tr className="tr-cl border-bottom">
												<th scope="col">
													<h2 className="text-xl font-semibold">Outras Movimentações Financeiras</h2>
												</th>
												<th scope="col" className="text-end">
													Valor
												</th>
											</tr>
										</thead>
										<tbody>
											{dataOutroCaixa.map((item, idx) => (
												<tr key={idx} className="tr-cscl">
													<td>{item.descricao}</td>
													<td className="text-end font-semibold italic">
														<small>{libFloat.strToCurrency(item.valor)}</small>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</section>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default Form;
